import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IIssueMachineHealthResponse } from '@kcftech/prometheus-client';
import { DateTimeDisplay } from 'components/date_time/DateTimeDisplay';
import { NoValue } from 'components/no_value/NoValue';
import { useRouteLocation } from 'contexts/RouteEntityContext';
import { IssueFaultTypesDisplay } from 'pages/Desk/issues/IssueFaultTypesDisplay';
import { HoverAvatar } from 'pages/Desk/issues/issue_card/HoverAvatar';
import { IssueFaultLocationPath } from 'pages/Desk/issues/issue_card/IssueFaultLocationPath';
import { PriorityLozenge } from 'pages/Desk/issues/issue_card/PriorityLozenge';
import { SeverityLozenge } from 'pages/Desk/issues/issue_card/SeverityLozenge';
import { FlagButton } from 'pages/Desk/issues/machine_health/FlagButton';
import { StatusLozenge } from 'pages/Desk/issues/StatusLozenge';
import {
    Actions,
    DateCreated,
    FaultLocation,
    FaultType,
    IssueNumber,
    LastUpdated,
    Priority,
    Severity,
    Status,
    TableRow,
} from 'pages/Location/issues/styled';
import { memo } from 'react';
import {
    IssuePreviewDescriptionAndRecommendation,
    IssuePreviewPopup,
} from 'shared/issue_preview_popup/IssuePreviewPopup';
import { SentryValidatedIndicator } from 'shared/sentry_validated_indicator/SentryValidatedIndicator';
import { css } from 'styled-components';
import { getIssueDetailsRoute } from 'utils/RouteHelper';

const issueNumberStyles = css`
    color: inherit;
    text-decoration: underline dotted;
    text-underline-offset: 0.2rem;
    &:hover {
        color: inherit;
    }
`;

const titleStyles = css`
    font-size: 1.4rem;
    font-weight: 500;
`;

type LocationIssuesRowProps = Readonly<{
    issue: IIssueMachineHealthResponse;
}>;

function LocationIssuesRowBase({ issue }: LocationIssuesRowProps) {
    const { location } = useRouteLocation();
    const issueUrl = getIssueDetailsRoute(location.Id, issue.Id);

    function renderTimestamp(date: Date | undefined) {
        if (!date) {
            return <NoValue />;
        }
        return (
            <div className="flex-col">
                <DateTimeDisplay date={date} showSeconds={false} />
            </div>
        );
    }

    return (
        <TableRow href={issueUrl} target="_blank" rel="noopener noreferrer">
            <IssueNumber>
                <FlagButton issue={issue} darkBackground />
                <IssuePreviewPopup issue={issue} issueNumberStyles={issueNumberStyles}>
                    <IssuePreviewDescriptionAndRecommendation
                        issue={issue}
                        titleStyles={titleStyles}
                    />
                </IssuePreviewPopup>
                <SentryValidatedIndicator issue={issue} />
            </IssueNumber>
            <Severity>
                <SeverityLozenge severity={issue.Severity} />
            </Severity>
            <Priority>
                <PriorityLozenge priority={issue.Priority} />
            </Priority>
            <Status>
                <StatusLozenge status={issue.IssueStatus.StatusType} />
            </Status>
            <FaultType>
                <IssueFaultTypesDisplay faultTypes={issue.FaultTypes} />
            </FaultType>
            <FaultLocation>
                <IssueFaultLocationPath issue={issue} />
            </FaultLocation>
            <DateCreated>{renderTimestamp(issue.Created)}</DateCreated>
            <LastUpdated>
                {renderTimestamp(issue.LastActivity)}
                {issue.LastActivity && <HoverAvatar user={issue.LastActivityBy} />}
            </LastUpdated>
            <Actions>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </Actions>
        </TableRow>
    );
}

export const LocationIssuesRow = memo(LocationIssuesRowBase);
